import React, { useEffect, useMemo, useState, lazy, createElement } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, LockIcon, CheckCircleIcon, BrainIcon, AwardIcon, LaptopIcon, DollarSignIcon, GraduationCapIcon } from 'lucide-react';
import { TrackingNoticeLinks } from '../components/TrackingNoticeLinks';
import { getUniversityBySlug, getProgramBySlug } from '../data/universityData';
import { useMetaTags } from '../hooks/useMetaTags';
import { getPublisherParams } from '../utils/publisherParams';
import { trackFormSubmit } from '../utils/trackFormSubmit';
import { getMetaCookies, resolveSub3 } from '../utils/metaCookies';
// Form value → API value mappings
const educationToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    'high-school': 1,
    'some-high-school': 2,
    ged: 3,
    'some-college': 5,
    associates: 5,
    bachelors: 7,
    masters: 7,
    doctorate: 7
  };
  return map[v] || 9;
};
const startTimeframeToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    immediately: 1,
    '1-3-months': 2,
    '4-6-months': 3,
    '3-6-months': 3,
    '6-12-months': 4,
    '1-year-plus': 4,
    undecided: 6
  };
  return map[v] || 6;
};
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'mobile';
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  return 'desktop';
};
export function AdvertiserTemplatePage() {
  const {
    collegeSlug,
    programSlug
  } = useParams<{
    collegeSlug: string;
    programSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const university = collegeSlug ? getUniversityBySlug(collegeSlug) : undefined;
  const initialProgram = university && programSlug ? getProgramBySlug(university, programSlug) : null;
  // Capture optional tracking params from the URL once at mount.
  // sub1/sub2/sub3 + the rest of the documented optional fields can all
  // be passed in via query string and will be forwarded on submit.
  const trackingParams = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return {
      sub1: q.get('sub1') || '',
      sub2: q.get('sub2') || '',
      sub3: q.get('sub3') || '',
      userIP: q.get('userIP') || '',
      webInitiatingURL: q.get('webInitiatingURL') || document.referrer || '',
      webLandingURL: q.get('webLandingURL') || window.location.href,
      inventoryType: q.get('inventoryType') || '',
      mediaType: q.get('mediaType') || '1'
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Inject LeadID + TrustedForm scripts AFTER first paint so they don't
  // compete with the initial render. They still load early enough to
  // observe user form interaction and produce a valid certificate.
  useEffect(() => {
    let leadIdScript: HTMLScriptElement | null = null;
    let tfScript: HTMLScriptElement | null = null;
    const inject = () => {
      ;
      (window as any).myCallbackFunction = function (token: string) {
        const field = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
        if (field) field.setAttribute('value', token);
      };
      leadIdScript = document.createElement('script');
      leadIdScript.id = 'LeadiDscript_campaign';
      leadIdScript.type = 'text/javascript';
      leadIdScript.async = true;
      leadIdScript.src = '//create.lidstatic.com/campaign/d50a3557-9854-7b8f-792c-527750401f5f.js?snippet_version=2&callback=myCallbackFunction';
      document.body.appendChild(leadIdScript);
      tfScript = document.createElement('script');
      tfScript.type = 'text/javascript';
      tfScript.async = true;
      tfScript.src = 'http' + ('https:' === document.location.protocol ? 's' : '') + '://api.trustedform.com/trustedform.js?provide_referrer=' + escape('false') + '&field=' + escape('xxTrustedFormCertUrl') + '&l=' + new Date().getTime() + Math.random();
      document.body.appendChild(tfScript);
    };
    const ric = (window as any).requestIdleCallback as ((cb: () => void, opts?: {
      timeout: number;
    }) => number) | undefined;
    const handle = ric ? ric(inject, {
      timeout: 2000
    }) : window.setTimeout(inject, 1200) as unknown as number;
    return () => {
      const cancelIdle = (window as any).cancelIdleCallback as ((h: number) => void) | undefined;
      if (ric && cancelIdle) cancelIdle(handle);else window.clearTimeout(handle);
      try {
        leadIdScript?.parentNode?.removeChild(leadIdScript);
      } catch {}
      try {
        tfScript?.parentNode?.removeChild(tfScript);
      } catch {}
    };
  }, []);
  const metaDescription = university ? initialProgram ? `Explore the ${initialProgram.degreeLevel ? `${initialProgram.degreeLevel} ` : ''}${initialProgram.name} program at ${university.name} and request information about your next steps.` : `Explore online degree programs at ${university.name} and request information about options that align with your educational goals.` : 'The requested university program could not be found. Explore accredited online degree programs on FindOnlineDegrees.';
  useMetaTags({
    title: university ? `${university.name} | Request Information` : 'Program Not Found',
    description: metaDescription,
    ogImage: university?.landingContent?.communityImage?.url,
    ogType: 'website',
    // Partner advertiser pages are paid lead-gen surfaces; keep them out
    // of the index so they don't compete with the brand site for SEO.
    robots: 'noindex, follow'
  });
  // Campus-aware advertisers (e.g. SJVC) use a step 0 to look up the user's
  // campus by zip before showing program options.
  const hasCampusFlow = Boolean(university?.campusZipLookup);
  const firstStep = hasCampusFlow ? 0 : 1;
  const totalSteps = hasCampusFlow ? 5 : 4;
  const [step, setStep] = useState(firstStep);
  const [formData, setFormData] = useState({
    degreeLevel: initialProgram?.degreeLevel || '',
    areaOfStudy: initialProgram?.category || '',
    program: initialProgram?.name || '',
    highestEducation: '',
    startDate: '',
    hsGradYear: '',
    zipCode: '',
    city: '',
    state: '',
    streetAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false,
    consentSms: false,
    campusId: 0,
    campusName: ''
  });
  const [error, setError] = useState('');
  const [campusLookupError, setCampusLookupError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Auto-advance Step 2
  useEffect(() => {
    if (step === 2 && formData.highestEducation && formData.startDate && formData.hsGradYear) {
      const timer = setTimeout(() => {
        setError('');
        setStep(3);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.highestEducation, formData.startDate, formData.hsGradYear, step]);
  // ZIP Code Lookup
  useEffect(() => {
    if (formData.zipCode.length === 5) {
      fetch(`https://api.zippopotam.us/us/${formData.zipCode}`).then(res => {
        if (res.ok) return res.json();
        throw new Error('Invalid zip');
      }).then(data => {
        if (data.places && data.places.length > 0) {
          setFormData(prev => ({
            ...prev,
            city: data.places[0]['place name'],
            state: data.places[0]['state abbreviation']
          }));
        }
      }).catch(() => {
        // Handle error silently
      });
    }
  }, [formData.zipCode]);
  if (!university) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-803" data-mp="27c5ep6jw49gc">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center" data-id="element-804" data-mp="27e8c4bndh4v3">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-805" data-mp="1xdy02ud2q3hi">
            Program Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-806" data-mp="1snho7eowipx9">
            We couldn't find the specific program or university you're looking
            for.
          </p>
          <button onClick={() => navigate('/')} className="w-full py-3 bg-gray-900 text-white rounded-lg font-bold hover:bg-gray-800 transition-colors" data-id="element-807" data-mp="1om5xf5uajynh">
            Return Home
          </button>
        </div>
      </div>;
  }
  const updateField = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  const handleContinue = () => {
    if (step === 0) {
      if (!formData.zipCode || formData.zipCode.length !== 5) {
        setCampusLookupError('Please enter your 5-digit ZIP code to continue.');
        return;
      }
      if (!formData.campusId) {
        setCampusLookupError(`Sorry, that ZIP code isn't served by a ${university.shortName} campus. Please check your ZIP and try again.`);
        return;
      }
      const campusPrograms = university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
      const preselectStillValid = !!initialProgram && campusPrograms.some(p => p.name === initialProgram.name);
      setFormData(prev => ({
        ...prev,
        degreeLevel: preselectStillValid ? initialProgram!.degreeLevel || '' : '',
        areaOfStudy: preselectStillValid ? initialProgram!.category : '',
        program: preselectStillValid ? initialProgram!.name : ''
      }));
      setCampusLookupError('');
      setStep(1);
      return;
    }
    if (step === 1) {
      if (university.singleProgramSelect) {
        if (!formData.program) {
          setError('Please select a program to continue.');
          return;
        }
      } else if (!formData.degreeLevel || !formData.areaOfStudy || !formData.program) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!formData.highestEducation || !formData.startDate || !formData.hsGradYear) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!formData.city || !formData.state) {
        setError('Please enter a valid ZIP code.');
        return;
      }
      if (!formData.streetAddress.trim()) {
        setError('Please enter your street address.');
        return;
      }
      setStep(4);
    }
  };
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) return formData.phone;
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  };
  const handleSubmit = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (formData.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!university.consentOnSubmit && !formData.consent) {
      setError('Please agree to the consent terms.');
      return;
    }
    if (university.tcpaTextSms && !formData.consentSms) {
      setError('Please check both consent boxes to continue.');
      return;
    }
    setIsSubmitting(true);
    // Read tracking values populated by the LeadID + TrustedForm scripts
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const tfInput = document.getElementsByName('xxTrustedFormCertUrl')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const trustedFormUrl = tfInput?.value || '';
    // Build the full lead payload per advertiser parameter spec.
    // Includes: required advertiser identifiers, mapped form values,
    // PII, and optional tracking parameters.
    const payload: Record<string, string> = {
      // Required advertiser identifiers. URL params (mPubGroupID, or
      // mPubID + mPubCampaignID) override the per-university config values
      // when present; otherwise fall back to the university config.
      ...getPublisherParams(location.search, {
        mPubID: university.mPubID || '',
        mPubCampaignID: university.mPubCampaignID || ''
      }),
      mBrandID: university.mBrandID || '',
      hpRequestId: generateUUID(),
      mSessionID: `s-${generateUUID()}`,
      leadIdToken,
      // Mapped form values. When the partner provides a numeric program
      // catalog (programId), post that ID as mProgram; otherwise fall back
      // to the program name.
      degreeInterest: formData.degreeLevel,
      areaStudy: formData.areaOfStudy,
      mProgram: (() => {
        const matched = programsForThisFlow.find(p => p.name === formData.program);
        if (matched?.mProgramValue) return matched.mProgramValue;
        return matched?.programId ? String(matched.programId) : formData.program;
      })(),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      gradYear: formData.hsGradYear,
      // PII
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ''),
      address: formData.streetAddress,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      campusLocation: formData.campusId ? String(formData.campusId) : '',
      smsConsent: formData.consentSms ? '1' : '0',
      // Optional tracking
      useragent: navigator.userAgent,
      sub1: trackingParams.sub1,
      sub2: trackingParams.sub2,
      // sub3 carries the Meta match keys ("<fbc>_<fbp>") for the Conversions
      // API when the Pixel cookies are present.
      sub3: resolveSub3(window.location.search) || trackingParams.sub3,
      userIP: trackingParams.userIP,
      webInitiatingURL: trackingParams.webInitiatingURL,
      webLandingURL: trackingParams.webLandingURL,
      trustedFormUrl,
      inventoryType: trackingParams.inventoryType,
      deviceType: getDeviceType(),
      mediaType: trackingParams.mediaType
    };
    // Meta match keys for server-side (CAPI) attribution.
    const {
      fbp,
      fbc
    } = getMetaCookies();
    if (fbp) payload.fbp = fbp;
    if (fbc) payload.fbc = fbc;
    console.log('[Advertiser Lead] payload:', payload);
    // Report the submission to GTM once (and only once) the lead has been
    // sent. `leadIdToken` doubles as the dedupe id for Meta CAPI.
    let reported = false;
    const reportSubmission = () => {
      if (reported) return;
      reported = true;
      trackFormSubmit({
        formId: 'university-request-info',
        pageUrl: window.location.pathname,
        leadId: leadIdToken
      });
    };
    // Host-and-post: the advertiser provides a posting endpoint per partner.
    // Configure it via university.leadPostUrl. If unset, no post is fired
    // (the payload is still logged above for QA / verification).
    const postBase = university.leadPostUrl || '';
    if (postBase) {
      const qs = new URLSearchParams(payload).toString();
      const fullUrl = `${postBase}?${qs}`;
      console.log('[Advertiser Lead] POST URL:', fullUrl);
      try {
        const pixel = new Image();
        pixel.onload = reportSubmission;
        pixel.src = fullUrl;
      } catch (err) {
        console.error('[Advertiser Lead] post error:', err);
      }
    } else {
      console.log('[Advertiser Lead] No leadPostUrl configured for this advertiser — skipping post.');
    }
    // The conversion is reported through GTM (dataLayer `form_submission`)
    // once the lead post is confirmed — no Meta Pixel event is fired here.
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      // Advertisers without a configured posting endpoint never produce a
      // pixel load, so confirm on the success state instead.
      reportSubmission();
      setTimeout(() => {
        navigate('/');
      }, 3000);
    }, 1000);
  };
  // Derived program list — honors campus-aware advertisers (filters to the
  // programs available at the user's matched campus).
  const programsForThisFlow = hasCampusFlow && formData.campusId && university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
  // Derived options for Step 1
  const uniqueAreas = Array.from(new Set(programsForThisFlow.filter(p => !formData.degreeLevel || p.degreeLevel === formData.degreeLevel).map(p => p.category)));
  const availablePrograms = programsForThisFlow.filter(p => (!formData.degreeLevel || p.degreeLevel === formData.degreeLevel) && (!formData.areaOfStudy || p.category === formData.areaOfStudy));
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 60
  }, (_, i) => currentYear - i);
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  if (isSuccess) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-808" data-mp="1bao4r531xeq8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 max-w-md w-full text-center" data-id="element-809" data-mp="1g7wqtgrbm2wr">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-810" data-mp="1l8u5vkh1agu1">
            <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-811" data-mp="2wlwpkoefjmif" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-812" data-mp="2wnze13zxp7l1">
            Thanks, {formData.firstName}!
          </h2>
          <p className="text-gray-600" data-id="element-813" data-mp="p04xbkco1gzv">
            {university.name} will reach out shortly. Redirecting you to the
            homepage...
          </p>
        </div>
      </div>;
  }
  // Dynamic hero title — show the selected program once chosen, otherwise a generic prompt.
  // On the first step (entry view), prefer the partner's high-CTR landing headline if provided.
  const isEntryStep = step === firstStep;
  // Once the user has chosen both a program and a degree level, surface the
  // degree in the H1 (e.g. "Bachelor's in Early Childhood Education").
  const selectedProgramTitle = formData.program && formData.degreeLevel ? `${formData.degreeLevel} in ${formData.program}` : formData.program || initialProgram?.name;
  const heroTitle = isEntryStep && university.landingContent ? university.landingContent.headline : selectedProgramTitle || `Find Your ${university.shortName} Program`;
  // Show the fuller landing subheadline on every step (entry and beyond) so
  // the program-selection / later steps aren't left with just the short
  // mission line. Falls back to the mission when no landing copy exists.
  const heroDescription = university.landingContent?.subheadline !== undefined ? university.landingContent.subheadline : university.mission;
  const featureIconMap: Record<string, any> = {
    BrainIcon,
    AwardIcon,
    LaptopIcon,
    DollarSignIcon,
    GraduationCapIcon
  };
  return <div className="min-h-screen font-sans flex flex-col bg-gray-50" data-id="element-814" data-mp="1wbtlilii3ieg">
      {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
      <input type="hidden" name="leadid_token" value="" data-id="element-815" data-mp="1n9aju4qgbbts" />
      <input type="hidden" name="xxTrustedFormCertUrl" value="" data-id="element-816" data-mp="1nbwsk7p2vxdl" />

      {/* White top nav bar with centered logo */}
      <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-817" data-mp="3hntniamqblde">
        <div className="max-w-7xl mx-auto flex items-center justify-center relative" data-id="element-818" data-mp="3hlqztx91u4bu">
          {university.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-819" data-mp="3k89hg5we1izn" /> : <span className="text-xl font-bold" style={{
          color: university.primaryColor
        }} data-id="element-820" data-mp="3tyclr28ymjda">
              {university.name}
            </span>}
          <div className="hidden md:flex items-center gap-1.5 text-gray-400 text-xs font-medium absolute right-0" data-id="element-821" data-mp="31agre46v53jo">
            <LockIcon className="w-3.5 h-3.5" data-id="element-822" data-mp="3699v54wclg6n" />
            Secure
          </div>
        </div>
      </header>

      {/* Branded main wrapper — neutral surface for the supporting sections */}
      <main className="flex-grow bg-white" data-id="element-823" data-mp="3g7pozfmrf3ju">
        {/* HERO — full-bleed brand color, optionally overlaid with a partner image */}
        <section className="relative py-12 md:py-20 px-4 bg-cover bg-center" style={{
        backgroundColor: university.primaryColor,
        backgroundImage: university.heroBackgroundImage ? `url(${university.heroBackgroundImage})` : undefined
      }} data-id="element-824" data-mp="qjgm8wnwjvw5">
          {/* Mobile-only eyebrow + H1 — rendered ABOVE the grid so the form
           can sit directly beneath it and stay above the fold on phones.
           Desktop renders the eyebrow + H1 inside the left column instead. */}
          <div className="relative max-w-6xl mx-auto lg:hidden text-white mb-6" data-id="element-825" data-mp="28u5ghs4sl8d6">
            <p className="text-sm font-semibold mb-3 text-white/90" data-id="element-826" data-mp="23uj0jaxkxe0o">
              {university.name}
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-tight" data-id="element-827" data-mp="2hmfpx77v59td">
              {heroTitle}
            </h1>
          </div>

          <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start" data-id="element-828" data-mp="2csusphe3gjv8">
            {/* Left: Branded hero copy — appears BELOW the form on mobile,
             beside it on desktop. */}
            <div className="text-white lg:pt-8 order-2 lg:order-1" data-id="element-829" data-mp="1azb691l99gbm">
              {/* Desktop-only eyebrow + H1 (mobile renders these above the grid). */}
              <p className="hidden lg:block text-base font-semibold mb-4 text-white/90" data-id="element-830" data-mp="1fsdzt9q18prt">
                {university.name}
              </p>
              <h1 className="hidden lg:block text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6" data-id="element-831" data-mp="15zotet9ca23c">
                {heroTitle}
              </h1>
              {heroDescription && <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-lg" data-id="element-832" data-mp="1yo479cgqied3">
                  {heroDescription}
                </p>}
              {university.landingContent?.heroHighlights && university.landingContent.heroHighlights.length > 0 && <ul className="mt-6 space-y-2.5 max-w-lg" data-id="element-833" data-mp="8bof1v74l9er">
                    {university.landingContent.heroHighlights.map(h => <li key={h} className="flex items-start gap-2.5 text-sm md:text-base text-white/90" data-id="element-834" data-mp="d9pzokk79gsc">
                        <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-white/70" data-id="element-835" data-mp="oqnyq4bc82st" />
                        <span data-id="element-836" data-mp="jtnn7dvpy7ub">{h}</span>
                      </li>)}
                  </ul>}
            </div>

            {/* Right: Form card — appears directly under the H1 on mobile
             (above the fold), to the right on desktop. */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-8 lg:p-10 order-1 lg:order-2" data-id="element-837" data-mp="17jrupwc12rql">
              {/* Progress Indicator */}
              <div className="mb-6" data-id="element-838" data-mp="xkstuzxi80e5">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" data-id="element-839" data-mp="xjive9mqoq6z">
                  {university.formHeading || 'Take the next step in your education.'}
                </h2>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-840" data-mp="3iai40zn9iy0i">
                  <div className="h-full transition-all duration-300" style={{
                  width: `${(step - firstStep + 1) / totalSteps * 100}%`,
                  backgroundColor: university.ctaColor
                }} data-id="element-841" data-mp="1xt907nptggmc" />
                </div>
              </div>

              {step > firstStep && <button onClick={() => {
              setStep(step - 1);
              setError('');
            }} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-6 transition-colors" data-id="element-842" data-mp="2hgq6jenke38r">
                  <ArrowLeftIcon className="w-4 h-4" data-id="element-843" data-mp="2qfbkewi3cu37" />
                  <span className="text-sm font-medium" data-id="element-844" data-mp="2vfg3muadw2x4">Back</span>
                </button>}

              {/* Step 0: Campus lookup by zip (campus-aware advertisers only) */}
              {step === 0 && hasCampusFlow && <div className="space-y-6" data-id="element-845" data-mp="114km32h81di7">
                  <p className="text-sm text-gray-600 text-center -mt-2" data-id="element-846" data-mp="t10hh1dw30sw">
                    Enter your ZIP code to see programs available at your{' '}
                    {university.shortName} campus.
                  </p>

                  <div data-id="element-847" data-mp="130rg9f9hg1ys">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-848" data-mp="12xvgdle2r37p">
                      ZIP Code
                    </label>
                    <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setCampusLookupError('');
                  // Auto-resolve campus when the zip becomes valid.
                  // If no match, leave the dropdown alone so the
                  // user can still pick manually.
                  if (val.length === 5 && university.campusZipLookup) {
                    const match = university.campusZipLookup(val);
                    if (match) {
                      setFormData(prev => ({
                        ...prev,
                        zipCode: val,
                        campusId: match.id,
                        campusName: match.name
                      }));
                      return;
                    }
                  }
                  updateField('zipCode', val);
                }} placeholder="e.g. 93291" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-lg" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-849" data-mp="1bzuxedp3a7an" />
                  </div>

                  <div data-id="element-850" data-mp="1o409hqkno5bb">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-851" data-mp="1o3go1zvsqysj">
                      Campus
                    </label>
                    <select value={formData.campusId || ''} disabled aria-readonly="true" tabIndex={-1} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg text-gray-700 appearance-none cursor-not-allowed" style={selectStyle as any} data-id="element-852" data-mp="1j32icoho40xo">
                      <option value="" data-id="element-853" data-mp="10cbe21cjf0q5">
                        -- Enter ZIP to match a campus --
                      </option>
                      {(university.landingContent?.campusList || []).map(c => <option key={c.id} value={c.id} data-id="element-854" data-mp="lgn21q0z99bp">
                            {c.name} Campus
                          </option>)}
                    </select>
                    <p className="mt-2 text-xs text-gray-500" data-id="element-855" data-mp="gb9s0jbay2gx">
                      Your campus is automatically determined by your ZIP code.
                    </p>
                  </div>

                  {campusLookupError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-856" data-mp="q9pgeeiy2xmq">
                      <p className="text-red-600 text-sm" data-id="element-857" data-mp="3oto2z23lqrlp">
                        {campusLookupError}
                      </p>
                    </div>}
                </div>}

              {/* Step 1: Program Selection — single dropdown variant */}
              {step === 1 && university.singleProgramSelect && <div className="space-y-6" data-id="element-858" data-mp="361v4wt9oeg74">
                  <div data-id="element-859" data-mp="3613myl904gzb">
                    <label htmlFor="program-select" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-860" data-mp="35ziponvayjxr">
                      Program of Interest
                    </label>
                    <select id="program-select" value={formData.program} onChange={e => {
                  const selected = programsForThisFlow.find(p => p.name === e.target.value);
                  updateField('program', e.target.value);
                  updateField('degreeLevel', selected?.degreeLevel || '');
                  updateField('areaOfStudy', selected?.category || '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-861" data-mp="2o9nbcf8ejgqf">
                      <option value="" data-id="element-862" data-mp="y06p89jr9jf8">-- Select Program --</option>
                      {programsForThisFlow.map(p => <option key={p.name} value={p.name} data-id="element-863" data-mp="1c0hg3cl2ey0e">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 1: Program Selection */}
              {step === 1 && !university.singleProgramSelect && <div className="space-y-6" data-id="element-864" data-mp="1upxh010nwshc">
                  <div data-id="element-865" data-mp="1uomfy8ylpytx">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-866" data-mp="1ur8ly45ug6a1">
                      Degree Level
                    </label>
                    <select value={formData.degreeLevel} onChange={e => {
                  updateField('degreeLevel', e.target.value);
                  updateField('areaOfStudy', '');
                  updateField('program', '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-867" data-mp="24omznw0dugyn">
                      <option value="" data-id="element-868" data-mp="1p2ie3bkllqp2">-- Select Degree Level --</option>
                      {university.degreeLevels.map(level => <option key={level} value={level} data-id="element-869" data-mp="1u024f9ukltxd">
                          {level}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-870" data-mp="3vthv26qnr8sc">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-871" data-mp="3vusyg5g6yeix">
                      Area of Study
                    </label>
                    <select value={formData.areaOfStudy} onChange={e => {
                  updateField('areaOfStudy', e.target.value);
                  updateField('program', '');
                }} disabled={!formData.degreeLevel} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-872" data-mp="3ji7ayw0mtrfd">
                      <option value="" data-id="element-873" data-mp="2hs281dmtusb0">-- Select Area of Study --</option>
                      {uniqueAreas.map(area => <option key={area} value={area} data-id="element-874" data-mp="9j46wz0zng2p">
                          {area}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-875" data-mp="3vtgj76grcwt3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-876" data-mp="3vpsow53ho6db">
                      Program
                    </label>
                    <select value={formData.program} onChange={e => updateField('program', e.target.value)} disabled={!formData.areaOfStudy} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-877" data-mp="sbybcz0zjpso">
                      <option value="" data-id="element-878" data-mp="2vif73il11ghv">-- Select Program --</option>
                      {availablePrograms.map(p => <option key={p.name} value={p.name} data-id="element-879" data-mp="3ag7n0buhk6wl">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 2: Academic Background */}
              {step === 2 && <div className="space-y-6" data-id="element-880" data-mp="2cr4i56yv3dsm">
                  <div data-id="element-881" data-mp="2ri3m24or5e5v">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-882" data-mp="2rjersa1rcvzm">
                      Highest Level of Education
                    </label>
                    <select value={formData.highestEducation} onChange={e => updateField('highestEducation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-883" data-mp="1z115n7psaygd">
                      <option value="" data-id="element-884" data-mp="20u3udrpk61b1">-- Please Select --</option>
                      <option value="high-school" data-id="element-885" data-mp="24tuq89txz7mf">
                        High School Diploma/GED
                      </option>
                      <option value="some-college" data-id="element-886" data-mp="29us80lb1hzne">Some College</option>
                      <option value="associates" data-id="element-887" data-mp="2et3gosplm441">Associate's Degree</option>
                      <option value="bachelors" data-id="element-888" data-mp="3l6j9ike8tv15">Bachelor's Degree</option>
                      <option value="masters" data-id="element-889" data-mp="3q6fg8rebs0ke">Master's Degree</option>
                      <option value="doctorate" data-id="element-890" data-mp="3q8s1bdfdqr1o">Doctorate</option>
                      <option value="some-high-school" data-id="element-891" data-mp="1ei0tfeoi9w0y">Some High School</option>
                    </select>
                  </div>

                  <div data-id="element-892" data-mp="19mbxrmflwz9x">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-893" data-mp="14t965jue3zwd">
                      When would you like to start classes?
                    </label>
                    <select value={formData.startDate} onChange={e => updateField('startDate', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-894" data-mp="zsleqx291h1m">
                      <option value="" data-id="element-895" data-mp="2b7rzmb65t7ml">-- Please Select --</option>
                      <option value="immediately" data-id="element-896" data-mp="1k4s8ycve60yw">Immediately</option>
                      <option value="1-3-months" data-id="element-897" data-mp="1p3lf1k8cmpp1">1-3 months</option>
                      <option value="3-6-months" data-id="element-898" data-mp="1bbx0laamnp4w">3-6 months</option>
                      <option value="6-12-months" data-id="element-899" data-mp="1bboovlq8qpva">6-12 months</option>
                      <option value="1-year-plus" data-id="element-900" data-mp="1fch02kiob4u7">1+ year</option>
                      <option value="undecided" data-id="element-901" data-mp="11e0skrczlab0">Undecided</option>
                    </select>
                  </div>

                  <div data-id="element-902" data-mp="16d3zkhcj1qlm">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-903" data-mp="16dn5ezowkoja">
                      High School Graduation Year
                    </label>
                    <select value={formData.hsGradYear} onChange={e => updateField('hsGradYear', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-904" data-mp="we4neosjzctv">
                      <option value="" data-id="element-905" data-mp="k33yrh6ccutz">-- Please Select --</option>
                      {years.map(year => <option key={year} value={year} data-id="element-906" data-mp="yv5tkmtk0xn5">
                          {year}
                        </option>)}
                      <option value="before-1965" data-id="element-907" data-mp="17u9abk5910yh">Before 1965</option>
                    </select>
                  </div>
                </div>}

              {/* Step 3: Location */}
              {step === 3 && <div className="space-y-6" data-id="element-908" data-mp="39lmgeciydew4">
                  {formData.city && formData.state ? <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-id="element-909" data-mp="34o454fhgqnky">
                      <span className="text-green-600 text-xl" data-id="element-910" data-mp="1kuxvj1lryx97">📍</span>
                      <div className="flex-1" data-id="element-911" data-mp="1fvbfkkekb2wp">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-912" data-mp="1tt9rzk9bop9k">
                          {formData.city}, {formData.state} {formData.zipCode}
                        </p>
                      </div>
                      <button type="button" onClick={() => {
                  updateField('city', '');
                  updateField('state', '');
                  updateField('zipCode', '');
                }} className="text-sm text-green-600 hover:text-green-700 font-semibold" data-id="element-913" data-mp="23r60s5esf84i">
                        change
                      </button>
                    </div> : <div data-id="element-914" data-mp="2hk5fw6anr0bj">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-915" data-mp="2hknhzjk048rd">
                        ZIP Code
                      </label>
                      <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  updateField('zipCode', val);
                }} placeholder="e.g. 90210" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-916" data-mp="364iltvbvo9fx" />
                    </div>}

                  {formData.city && formData.state && <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-917" data-mp="57omdfhjcdf2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-918" data-mp="55vejwv6rn5h">
                        Street Address
                      </label>
                      <input type="text" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} placeholder="123 Main St" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-919" data-mp="3rg7po0z7yxua" />
                    </div>}
                </div>}

              {/* Step 4: Contact & Submit */}
              {step === 4 && <div className="space-y-6" data-id="element-920" data-mp="1j56z8cgrncon">
                  <p className="text-sm text-gray-600 text-center -mt-2" data-id="element-921" data-mp="1o64eag6hbqlx">
                    Where should {university.shortName} send your information?
                  </p>

                  <div className="grid grid-cols-2 gap-4" data-id="element-922" data-mp="1xw62r92qvb1d">
                    <div data-id="element-923" data-mp="1xvo11wxbc4m2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-924" data-mp="1xyig18xk4x83">
                        First Name
                      </label>
                      <input type="text" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-925" data-mp="26y5ky4phwl5w" />
                    </div>
                    <div data-id="element-926" data-mp="3s8ytzb3z62mc">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-927" data-mp="3nchx9sipmfxn">
                        Last Name
                      </label>
                      <input type="text" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-928" data-mp="2zmxaszncl0jm" />
                    </div>
                  </div>

                  <div data-id="element-929" data-mp="w60pdoulndcf">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-930" data-mp="w6is94broptb">
                      Email Address
                    </label>
                    <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-931" data-mp="mfo8c6lzu2b1" />
                  </div>

                  <div data-id="element-932" data-mp="3lthxp0blitbf">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-933" data-mp="3lxfevgem5aso">
                      Phone Number
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => updateField('phone', formatPhoneNumber(e.target.value))} placeholder="555-123-4567" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-934" data-mp="1wcw2qrg8wiov" />
                  </div>

                  <div className="pt-4 space-y-3" data-id="element-935" data-mp="yqf6zqn5ud58">
                    {university.tcpaTextSms && <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-936" data-mp="2y3t21mvh1jkk">
                        <input type="checkbox" checked={formData.consentSms} onChange={e => updateField('consentSms', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-937" data-mp="2t23s6ajzjby2" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-938" data-mp="2mnen6ye67e7m">
                          {university.tcpaTextSms}
                        </span>
                      </label>}
                    {university.consentOnSubmit ?
                // Consent is given by clicking the submit button, so
                // the disclosure is shown as plain text.
                <p className="text-xs text-gray-600 leading-relaxed p-4 rounded-lg border border-gray-200 bg-gray-50" data-id="element-939" data-mp="bv3pyob4mn8t">
                        {university.tcpaText}
                      </p> : <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-940" data-mp="szn6jwvoezao">
                        <input type="checkbox" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-941" data-mp="11yidxcagiffk" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-942" data-mp="3rgr8arter6fp">
                          {university.tcpaText || `By checking this box, I consent to receive calls and text messages from ${university.name} at the number provided above regarding educational opportunities. I understand that these calls may be generated using an automated technology and that my consent is not required to enroll. Message and data rates may apply.`}
                        </span>
                      </label>}
                  </div>
                </div>}

              {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-943" data-mp="1o7fqkcapfmuz">
                  <p className="text-red-600 text-sm" data-id="element-944" data-mp="1o52zn9myy25s">{error}</p>
                </div>}

              <div className="mt-8" data-id="element-945" data-mp="1aa0wgu9iaqwo">
                {step < 4 ? <button onClick={handleContinue} className="w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-946" data-mp="1e9ru9hxga7ap">
                    Continue
                  </button> : <button onClick={handleSubmit} disabled={isSubmitting || !university.consentOnSubmit && !formData.consent || Boolean(university.tcpaTextSms) && !formData.consentSms} className="w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-947" data-mp="o773319rajrd">
                    {isSubmitting ? 'Submitting...' : university.submitLabel || 'Submit Application'}
                  </button>}
              </div>
            </div>
          </div>
        </section>
        {/* /HERO */}

        {/* Supporting site content — renders on every step so the
            program page (deep link) reads as a full marketing site,
            not just a form. */}
        {university.landingContent && <>
            {/* Stats strip */}
            {university.landingContent.stats && <section className="bg-white border-b border-gray-100 py-10 md:py-14 px-4" data-id="element-948" data-mp="3p1n89ah8t7lq">
                <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-949" data-mp="3k2tx3opfhsoq">
                  {university.landingContent.stats.map(s => <div key={s.label} className="text-center" data-id="element-950" data-mp="3b3gjqky6ojyq">
                      <div className="text-3xl md:text-4xl font-extrabold mb-1" style={{
                color: university.primaryColor
              }} data-id="element-951" data-mp="24opbfw6qdwuo">
                        {s.value}
                      </div>
                      <div className="text-xs md:text-sm font-semibold uppercase tracking-wider text-gray-500" data-id="element-952" data-mp="265la8fb8t0ku">
                        {s.label}
                      </div>
                    </div>)}
                </div>
              </section>}

            {/* Why us — feature grid */}
            <section className="bg-gray-50 py-16 md:py-24 px-4" data-id="element-953" data-mp="1iez1smxbteab">
              <div className="max-w-6xl mx-auto" data-id="element-954" data-mp="1n67ebrtydfrp">
                <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-955" data-mp="1n820takvhfxw">
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
                color: university.ctaColor
              }} data-id="element-956" data-mp="18ima1ulcdai7">
                    Why {university.shortName}
                  </p>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight" data-id="element-957" data-mp="14g96ncu2hool">
                    Education built around your career goals.
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-958" data-mp="fw4poe28vbbr">
                  {university.landingContent.features.map(f => {
                const Icon = featureIconMap[f.icon] || GraduationCapIcon;
                return <div key={f.title} className="bg-white rounded-xl border border-gray-200 p-6 text-center hover:shadow-md transition-shadow" data-id="element-959" data-mp="13t1zu1g8f8wu">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{
                    backgroundColor: `${university.ctaColor}15`
                  }} data-id="element-960" data-mp="1djd3rnzx1i3q">
                          <Icon className="w-7 h-7" style={{
                      color: university.ctaColor
                    }} data-id="element-961" data-mp="1hkymhpz4sqlu" />
                        </div>
                        <h3 className="text-sm font-bold tracking-widest uppercase text-gray-900 mb-2" data-id="element-962" data-mp="3gxf3z51ttggg">
                          {f.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-963" data-mp="3prk3ecv1007l">
                          {f.description}
                        </p>
                      </div>;
              })}
                </div>
              </div>
            </section>

            {/* Info sections — alternating layout */}
            {university.landingContent.infoSections && university.landingContent.infoSections.map((s, i) => <section key={s.heading} className={`py-16 md:py-24 px-4 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-id="element-964" data-mp="hcrx609vz777" data-mpi={i}>
                  <div className="max-w-5xl mx-auto" data-id="element-965" data-mp="md68psap4z9z">
                    {s.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
              color: university.ctaColor
            }} data-id="element-966" data-mp="wh4hetzz6hr3">
                        {s.eyebrow}
                      </p>}
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5 max-w-3xl" data-id="element-967" data-mp="dpbo0ygzuuiu">
                      {s.heading}
                    </h2>
                    <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 max-w-3xl" data-id="element-968" data-mp="8s1ylitluiv0">
                      {s.body}
                    </p>
                    {s.bullets && <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl" data-id="element-969" data-mp="3vwrpi1dgtjfu">
                        {s.bullets.map(b => <li key={b} className="flex items-start gap-3 text-gray-700" data-id="element-970" data-mp="1u43j5j5iyfvu">
                            <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                  color: university.ctaColor
                }} data-id="element-971" data-mp="3sc7r5u2iyq79" />
                            <span className="text-sm md:text-base" data-id="element-972" data-mp="3ikbn82drc5v4">{b}</span>
                          </li>)}
                      </ul>}
                  </div>
                </section>)}

            {/* Community image — partner-supplied lifestyle photo */}
            {university.landingContent.communityImage && <section className="bg-white py-16 md:py-24 px-4" data-id="element-973" data-mp="63g7v7tf8kcm">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center" data-id="element-974" data-mp="2lw5n9srs61n6">
                  <div className="order-2 md:order-1" data-id="element-975" data-mp="2qw9vkvijbq2b">
                    {university.landingContent.communityImage.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
                color: university.ctaColor
              }} data-id="element-976" data-mp="2igklujbtj0jw">
                        {university.landingContent.communityImage.eyebrow}
                      </p>}
                    {university.landingContent.communityImage.heading && <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-977" data-mp="1uporbefz9r8y">
                        {university.landingContent.communityImage.heading}
                      </h2>}
                    {university.landingContent.communityImage.body && <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-978" data-mp="1gwgxrh8c6lom">
                        {university.landingContent.communityImage.body}
                      </p>}
                  </div>
                  <div className="order-1 md:order-2 flex justify-center" data-id="element-979" data-mp="qgxckzx71r5c">
                    <img src={university.landingContent.communityImage.url} alt={university.landingContent.communityImage.alt || university.name} loading="lazy" decoding="async" className="w-full max-w-md object-contain" data-id="element-980" data-mp="veyw1lyj7shc" />
                  </div>
                </div>
              </section>}

            {/* Testimonial */}
            {university.landingContent.testimonial && <section className="py-16 md:py-24 px-4" style={{
          backgroundColor: `${university.primaryColor}08`
        }} data-id="element-981" data-mp="1o1thom734xwe">
                <div className="max-w-3xl mx-auto text-center" data-id="element-982" data-mp="1x4kqee8ktfkc">
                  <div className="text-6xl leading-none mb-4 font-serif" style={{
              color: university.primaryColor
            }} data-id="element-983" data-mp="3hlbx6rbjdepj">
                    "
                  </div>
                  <blockquote className="text-xl md:text-2xl font-medium text-gray-900 leading-relaxed mb-6" data-id="element-984" data-mp="38mzv9t57yuhs">
                    {university.landingContent.testimonial.quote}
                  </blockquote>
                  <div className="text-sm text-gray-600" data-id="element-985" data-mp="569o3axwibei">
                    <span className="font-bold text-gray-900" data-id="element-986" data-mp="656fhl9bamp">
                      {university.landingContent.testimonial.name}
                    </span>
                    {' — '}
                    {university.landingContent.testimonial.program}
                  </div>
                </div>
              </section>}

            {/* Career section with image */}
            {university.landingContent.careerSection && <section className="bg-white py-16 md:py-24 px-4" data-id="element-987" data-mp="j24y0raboc5r">
                <div className="max-w-6xl mx-auto rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-988" data-mp="10rzkn9vknzzd">
                  <div className="grid grid-cols-1 md:grid-cols-2 items-stretch" data-id="element-989" data-mp="vqsc38lluwrn">
                    <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-990" data-mp="qts7710hs024">
                      <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-991" data-mp="ms705wan2fzu">
                        {university.landingContent.careerSection.headline}
                      </h2>
                      <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6" data-id="element-992" data-mp="1ejml7lwjruny">
                        {university.landingContent.careerSection.body}
                      </p>
                      <button onClick={() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }} className="self-start px-7 py-3 text-white font-bold rounded-lg transition-all shadow-md hover:shadow-lg" style={{
                  backgroundColor: university.ctaColor
                }} data-id="element-993" data-mp="15p855h470g3s">
                        Get Started
                      </button>
                    </div>
                    <div className="min-h-[280px] md:min-h-0" data-id="element-994" data-mp="3u5jhua2i9eg1">
                      <img src={university.landingContent.careerSection.imageUrl} alt={university.landingContent.careerSection.imageAlt || university.name} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-995" data-mp="2ygr1jqkxa6c" />
                    </div>
                  </div>
                </div>
              </section>}

            {/* Final CTA band */}
            {university.landingContent.finalCta && <section className="py-16 md:py-20 px-4" style={{
          backgroundColor: university.primaryColor
        }} data-id="element-996" data-mp="2mqnw7w57cz5l">
                <div className="max-w-4xl mx-auto text-center text-white" data-id="element-997" data-mp="2whijn3u6q4sm">
                  <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4" data-id="element-998" data-mp="2rofxvhvnfzmz">
                    {university.landingContent.finalCta.headline}
                  </h2>
                  <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-999" data-mp="31ftunhw7ulcf">
                    {university.landingContent.finalCta.body}
                  </p>
                  <button onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }} className="px-8 py-4 font-bold rounded-lg text-base md:text-lg transition-all shadow-lg hover:shadow-xl" style={{
              backgroundColor: university.ctaColor,
              color: '#fff'
            }} data-id="element-1000" data-mp="3afgwg4suxt60">
                    {university.landingContent.finalCta.buttonLabel || 'Request Information'}
                  </button>
                </div>
              </section>}
          </>}

        {/* Branded footer */}
        <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs" data-id="element-1001" data-mp="39161gg87ffg8">
          <div className="max-w-5xl mx-auto space-y-2" data-id="element-1002" data-mp="3dvthdqp571j7">
            <p data-id="element-1003" data-mp="2z1oza8s5vjp0">
              © {new Date().getFullYear()} {university.name}. All rights
              reserved.
            </p>
            <p className="text-gray-500" data-id="element-1004" data-mp="33zyv8llpmobo">
              {university.accreditation} • Founded {university.founded}
            </p>
            <div className="max-w-2xl mx-auto pt-2" data-id="element-1005" data-mp="128ddu5rj7d4y">
              <TrackingNoticeLinks data-id="element-1006" data-mp="1272d6etduli2" />
            </div>
          </div>
        </footer>
      </main>
    </div>;
}